import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import { makeTree } from 'app/shared/components/common/Tree';
import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';

import { ChecklistDisplaySettingType } from 'constants/checklists';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { StatusEnum } from 'constants/status';
import { getChecklistResultStatus } from 'utils/checklist';
import { getConditionalObject } from 'utils/data';

import { getCategoryRow, getItemRow } from './Alerts.config';

// A hidden checklist is absent from `availableSubtabs` by design (it never renders as a subtab) yet its
// suppressed-fields deep link must stay reachable — DEV-5146 — so the raw snapshot set, not the subtab
// map, decides whether the route still resolves. PENDING_CHANGES_SUBTAB also lands here via `subtab`
// (the "See Changes" button) but is never a checklist id — redirecting it back to a real checklist is
// what silently closed the drawer right after it opened (FH-3005).
export const resolveRedirectChecklistId = ({
  isLoading,
  subtab,
  snapshotChecklistIds,
  availableSubtabs,
}) => {
  const [firstAvailableChecklistId] = Object.keys(availableSubtabs);

  if (
    isLoading ||
    !firstAvailableChecklistId ||
    subtab === PENDING_CHANGES_SUBTAB ||
    snapshotChecklistIds.has(subtab)
  ) {
    return null;
  }

  return firstAvailableChecklistId;
};

export const orderRows = ({ rows, order }) =>
  _.orderBy(
    rows,
    [
      row => {
        const value = _.get(row, order.id);

        return order.id === 'rowName' ? _.toLower(value) : value;
      },
    ],
    [order.direction],
  );

const filterItems = ({ items, itemsMap, itemsWithPendingChanges }) => {
  return items.reduce((acc, item) => {
    const itemData = itemsMap[item.id];

    if (itemData) {
      const pendingChange = itemsWithPendingChanges.find(({ id }) => id === item.id);

      acc.push({
        ...item,
        pendingChangeStatus: itemData.pendingChangeStatus,
        isPendingChange: !!pendingChange,
        isManuallyChanged: !!pendingChange?.isManuallyChanged,
        calculation: itemData,
        rowName: item.name,
        orderSequence: item.orderSequence,
      });
    }

    return acc;
  }, []);
};

// An item flagged hideOnLoanSummary still evaluates and rolls up into its parent, and still prints on the
// Compliance Review Report — it is only left out of this summary, together with everything nested under it.
const dropItemsHiddenOnLoanSummary = items => {
  const childrenByParentId = _.groupBy(items, 'parentId');
  const collectSubtreeIds = ids =>
    ids.flatMap(id => [id, ...collectSubtreeIds(_.map(childrenByParentId[id], 'id'))]);
  const hiddenIds = new Set(collectSubtreeIds(_.map(_.filter(items, 'hideOnLoanSummary'), 'id')));

  return hiddenIds.size ? items.filter(item => !hiddenIds.has(item.id)) : items;
};

const processItems = ({ orderDirection, orderId, items, itemsMap, itemsWithPendingChanges }) => {
  if (!items) {
    return [];
  }

  const filteredItems = filterItems({
    items: dropItemsHiddenOnLoanSummary(items),
    itemsMap,
    itemsWithPendingChanges,
  });
  const orderedItems = orderRows({
    rows: filteredItems,
    order: {
      direction: orderDirection,
      id: orderId,
    },
  });

  return makeTree(orderedItems);
};

export const filterCategories = ({
  categories,
  categoriesMap,
  itemsMap,
  orderDirection,
  orderId,
  disabled,
  statusDisplayType,
  categoriesIdsWithPendingChanges,
  itemsWithPendingChanges,
  isChecklistRecalculating,
}) => {
  return categories.reduce(
    (acc, category) => {
      const isCategoryPendingChange = categoriesIdsWithPendingChanges.includes(category.id);

      if (category.isRoot) {
        const processedItems = processItems({
          orderDirection,
          items: category.items,
          itemsMap,
          orderId,
          itemsWithPendingChanges,
        });
        acc.rootCategory = {
          ...category,
          isPendingChange: isCategoryPendingChange,
          items: processedItems,
        };

        return acc;
      }

      const categoryData = categoriesMap[category.id];

      if (categoryData) {
        const processedItems = processItems({
          orderDirection,
          items: category.items,
          itemsMap,
          orderId,
          itemsWithPendingChanges,
        });

        if (!processedItems.length) {
          return acc;
        }

        acc.categories.push({
          ...getCategoryRow({
            category: {
              ...categoryData,
              ...category,
              isPendingChange: isCategoryPendingChange,
              items: processedItems,
              disabled,
            },
            statusDisplayType,
            isChecklistRecalculating,
          }),
          rowName: category.name,
          orderSequence: category.orderSequence,
        });
      }

      return acc;
    },
    { categories: [], rootCategory: null, statusDisplayType },
  );
};

export const mapItemsToRows = ({
  items,
  disabled,
  statusDisplayType,
  isChecklistRecalculating,
}) => {
  const itemsCount = items.length;

  return items.map((item, index) => ({
    ...getItemRow({
      isLast: itemsCount === index + 1,
      item,
      disabled,
      statusDisplayType,
      isChecklistRecalculating,
    }),
    rowName: item.name,
    orderSequence: item.orderSequence,
  }));
};

const IN_PROGRESS_COLOR_MATCH = {
  backgroundColor: 3,
  color: 9,
  borderColor: 3,
};

export const getChecklistStatusLabelProps = ({
  statusDisplayType,
  status,
  state,
  isChecklistRecalculating,
}) => {
  const isFinalResultDisplayType = statusDisplayType === ChecklistDisplaySettingType.FINAL_RESULT;

  // A checklist still running its (re)calculation has no result to claim yet — the FinalResult
  // branch below would otherwise read a falsy status as Failed while the left-menu spinner turns.
  if (isChecklistRecalculating) {
    return {
      variant: LabelVariant.ORANGE,
      title: 'Calculating',
      colorMatch: IN_PROGRESS_COLOR_MATCH,
    };
  }

  const { text, labelVariant, iconType } = getChecklistResultStatus({
    state,
    result: status,
    statusDisplayType: isFinalResultDisplayType
      ? ChecklistDisplaySettingType.FINAL_RESULT
      : ChecklistDisplaySettingType.PROGRESS_RESULT,
  });

  return {
    variant: labelVariant,
    title: text,
    ...getConditionalObject(!isFinalResultDisplayType && iconType === StatusEnum.WARNING, {
      colorMatch: IN_PROGRESS_COLOR_MATCH,
    }),
  };
};

export const getStatusIconPendingChangeWrapperProps = isPendingChange => ({
  pendingChangesActionType: isPendingChange ? PendingChangesActionType.MODIFIED : undefined,
  textSelector: null,
  sx: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: 'auto',
  },
});
